package top.wcpe.allincore.dsl.ir.canvas

import kotlin.jvm.JvmStatic

/**
 * draw 指令字节码的对外门面。
 *
 * 历史上本类承担：opcode 常量、`Operand` / `Instruction` 嵌套类、
 * 操作数校验、字节长度计算、`encode/decode`、控制流与高级图元工厂方法。
 * M11.x 重构将其按职责拆为：
 * - [DrawOpcodes] —— opcode 与操作数 kind 常量
 * - [Operand] / [Instruction] —— 顶级化的 IR 模型
 * - [DrawBlobSpec] —— 操作数个数、变长校验、单条指令字节长度
 * - [DrawBlobEncoder] / [DrawBlobDecoder] —— 编解码
 * - [InstructionFactory] —— 控制流与高级图元工厂方法
 *
 * 本类只保留对外 API 的静态门面（同名常量 + 同名静态方法转发），
 * 行为与原实现完全一致，便于历史调用方继续通过 `DrawBlob.xxx` 访问。
 *
 * 二进制布局（v10 起，未变）：
 * ```
 * u16 instructionCount
 * repeat instructionCount:
 *   u8  opcode
 *   N × Operand           // N 由 opcode 决定
 *     Operand: u8 kind | payload
 *       kind=0 LITERAL : 8B double
 *       kind=1 EXPR    : u16 byteLen | byte[byteLen]   // ExprBlob
 *       kind=2 STR     : u16 byteLen | byte[byteLen]   // UTF-8
 * ```
 */
object DrawBlob {

    // ===== 转发常量：opcode =====

    const val OP_RECT: Byte = DrawOpcodes.OP_RECT
    const val OP_CIRCLE: Byte = DrawOpcodes.OP_CIRCLE
    const val OP_DOT: Byte = DrawOpcodes.OP_DOT
    const val OP_LINE: Byte = DrawOpcodes.OP_LINE
    const val OP_ARC: Byte = DrawOpcodes.OP_ARC
    const val OP_RING: Byte = DrawOpcodes.OP_RING
    const val OP_ROUND_RECT: Byte = DrawOpcodes.OP_ROUND_RECT
    const val OP_TEXT: Byte = DrawOpcodes.OP_TEXT
    const val OP_POLYGON: Byte = DrawOpcodes.OP_POLYGON
    const val OP_PATH: Byte = DrawOpcodes.OP_PATH
    const val OP_DRAW_IMAGE: Byte = DrawOpcodes.OP_DRAW_IMAGE
    const val OP_BEZIER_QUAD: Byte = DrawOpcodes.OP_BEZIER_QUAD
    const val OP_BEZIER_CUBIC: Byte = DrawOpcodes.OP_BEZIER_CUBIC

    /** v4 B.4：复合路径 opcode（变长，承载 PathCommand 节点序列）。 */
    const val OP_PATH_BUILD: Byte = DrawOpcodes.OP_PATH_BUILD

    /** v4 B.5：像素位图 opcode（小地图前置）。 */
    const val OP_BITMAP: Byte = DrawOpcodes.OP_BITMAP

    const val OP_VAL_ASSIGN: Byte = DrawOpcodes.OP_VAL_ASSIGN
    const val OP_FOR_BEGIN: Byte = DrawOpcodes.OP_FOR_BEGIN
    const val OP_FOR_END: Byte = DrawOpcodes.OP_FOR_END
    const val OP_IF_BEGIN: Byte = DrawOpcodes.OP_IF_BEGIN
    const val OP_IF_END: Byte = DrawOpcodes.OP_IF_END

    const val LOCAL_SLOT_COUNT: Int = DrawOpcodes.LOCAL_SLOT_COUNT

    const val OPERAND_LITERAL: Byte = DrawOpcodes.OPERAND_LITERAL
    const val OPERAND_EXPR: Byte = DrawOpcodes.OPERAND_EXPR
    const val OPERAND_STR: Byte = DrawOpcodes.OPERAND_STR

    /** v4 B.5：byte[] 字节数组操作数 kind。 */
    const val OPERAND_BYTES: Byte = DrawOpcodes.OPERAND_BYTES

    // ===== 转发元数据查询 =====

    /** 变长指令判定（POLYGON / PATH）。 */
    @JvmStatic
    fun isVariadic(opcode: Byte): Boolean = DrawBlobSpec.isVariadic(opcode)

    /** 控制流 opcode 判定。 */
    @JvmStatic
    fun isControlFlow(opcode: Byte): Boolean = DrawBlobSpec.isControlFlow(opcode)

    /** 各 opcode 的固定操作数个数（不适用于变长指令 POLYGON/PATH）。 */
    @JvmStatic
    fun expectedOperandCount(opcode: Byte): Int = DrawBlobSpec.expectedOperandCount(opcode)

    /** 单条指令编码后的字节长度（包含 opcode 本身）。 */
    @JvmStatic
    fun instructionByteLength(ins: Instruction): Int = DrawBlobSpec.instructionByteLength(ins)

    // ===== 转发编解码 =====

    /** 将指令序列编码为字节数组。 */
    @JvmStatic
    fun encode(instructions: List<Instruction>): ByteArray = DrawBlobEncoder.encode(instructions)

    /** 从字节数组解码指令序列。 */
    @JvmStatic
    fun decode(bytes: ByteArray): List<Instruction> = DrawBlobDecoder.decode(bytes)

    // ===== 转发工厂方法 =====

    @JvmStatic
    fun valAssign(slotIndex: Int, value: Operand): Instruction =
        InstructionFactory.valAssign(slotIndex, value)

    @JvmStatic
    fun forBegin(slotIndex: Int, iterCount: Int, bodyByteLength: Int): Instruction =
        InstructionFactory.forBegin(slotIndex, iterCount, bodyByteLength)

    @JvmStatic
    fun forEnd(): Instruction = InstructionFactory.forEnd()

    @JvmStatic
    fun ifBegin(condition: Operand, thenByteLength: Int, elseByteLength: Int): Instruction =
        InstructionFactory.ifBegin(condition, thenByteLength, elseByteLength)

    @JvmStatic
    fun ifEnd(): Instruction = InstructionFactory.ifEnd()

    @JvmStatic
    fun arc(cx: Double, cy: Double, radius: Double, startAngle: Double, endAngle: Double, color: Int): Instruction =
        InstructionFactory.arc(cx, cy, radius, startAngle, endAngle, color)

    @JvmStatic
    fun ring(cx: Double, cy: Double, radius: Double, width: Double, color: Int): Instruction =
        InstructionFactory.ring(cx, cy, radius, width, color)

    @JvmStatic
    fun roundRect(x: Double, y: Double, w: Double, h: Double, radius: Double, color: Int): Instruction =
        InstructionFactory.roundRect(x, y, w, h, radius, color)

    @JvmStatic
    fun text(x: Double, y: Double, size: Double, color: Int, content: String): Instruction =
        InstructionFactory.text(x, y, size, color, content)

    @JvmStatic
    fun polygon(vertices: DoubleArray, color: Int): Instruction =
        InstructionFactory.polygon(vertices, color)

    @JvmStatic
    fun path(vertices: DoubleArray, width: Double, color: Int, closed: Boolean): Instruction =
        InstructionFactory.path(vertices, width, color, closed)

    @JvmStatic
    fun drawImage(x: Double, y: Double, w: Double, h: Double, resourceId: String): Instruction =
        InstructionFactory.drawImage(x, y, w, h, resourceId)

    /** v4 B.3：构造 OP_BEZIER_QUAD 指令（二次贝塞尔描边）。 */
    @JvmStatic
    fun bezierQuad(
        p0x: Double,
        p0y: Double,
        c1x: Double,
        c1y: Double,
        p1x: Double,
        p1y: Double,
        width: Double,
        color: Int,
    ): Instruction = InstructionFactory.bezierQuad(p0x, p0y, c1x, c1y, p1x, p1y, width, color)

    /** v4 B.3：构造 OP_BEZIER_CUBIC 指令（三次贝塞尔描边）。 */
    @JvmStatic
    fun bezierCubic(
        p0x: Double,
        p0y: Double,
        c1x: Double,
        c1y: Double,
        c2x: Double,
        c2y: Double,
        p1x: Double,
        p1y: Double,
        width: Double,
        color: Int,
    ): Instruction = InstructionFactory.bezierCubic(p0x, p0y, c1x, c1y, c2x, c2y, p1x, p1y, width, color)

    /**
     * v4 B.4：构造 OP_PATH_BUILD 指令（复合路径，节点序列 + mode + width + color）。
     * 详见 [InstructionFactory.pathBuild]。
     */
    @JvmStatic
    fun pathBuild(kinds: ByteArray, coordOperands: Array<Operand>, mode: Int, width: Double, color: Int): Instruction =
        InstructionFactory.pathBuild(kinds, coordOperands, mode, width, color)

    /**
     * v4 B.5：构造 OP_BITMAP 指令（像素位图，小地图前置）。
     * 详见 [InstructionFactory.bitmap]。
     */
    @JvmStatic
    fun bitmap(
        data: ByteArray,
        srcW: Int,
        srcH: Int,
        paletteId: Int,
        dstX: Double,
        dstY: Double,
        dstW: Double,
        dstH: Double,
    ): Instruction = InstructionFactory.bitmap(data, srcW, srcH, paletteId, dstX, dstY, dstW, dstH)
}
